import type { Object3D } from 'three';

import type { SceneComposition } from '../../types/engine.types';
import type { SceneDiffManagerConfig, ObjectSnapshot, SceneDiff } from './types';
import { snapshotSceneCfn } from './functions/snapshot-scene.cfn';
import { computeDiffCfn } from './functions/compute-diff.cfn';

/**
 * SceneDiffManager tracks scene state and computes diffs between old and new compositions.
 *
 * This enables incremental scene updates with animated transitions instead of
 * full scene rebuilds.
 *
 * @example
 * ```typescript
 * const diffManager = new SceneDiffManager();
 *
 * // Capture current scene state
 * diffManager.snapshotScene(objectHolder);
 *
 * // Build new composition
 * const newComposition = await builder.build(newData);
 *
 * // Compute what changed
 * const diff = diffManager.computeDiff(newComposition);
 *
 * // Use diff for animated transitions
 * // diff.removed -> fade out
 * // diff.moved -> animate position
 * // diff.added -> fade in
 * ```
 */
export class SceneDiffManager {
	#config: SceneDiffManagerConfig;
	#currentSnapshot: Map<string, ObjectSnapshot> = new Map();

	constructor(config: SceneDiffManagerConfig = {}) {
		this.#config = {
			positionTolerance: config.positionTolerance ?? 0.1
		};
	}

	// ── Internal state for cfn access ────────────────────────────────────────
	/** @internal */ get internalConfig(): SceneDiffManagerConfig {
		return this.#config;
	}
	/** @internal */ get internalCurrentSnapshot(): Map<string, ObjectSnapshot> {
		return this.#currentSnapshot;
	}

	/**
	 * Capture current scene state
	 * @param objectHolder The container holding all scene objects
	 */
	snapshotScene(objectHolder: Object3D): void {
		snapshotSceneCfn(this, { objectHolder });
	}

	/**
	 * Compute diff between current snapshot and new composition
	 */
	computeDiff(newComposition: SceneComposition): SceneDiff {
		return computeDiffCfn(this, { newComposition });
	}

	/**
	 * Check if scene has any tracked objects
	 */
	hasSnapshot(): boolean {
		return this.#currentSnapshot.size > 0;
	}

	/**
	 * Clear the current snapshot
	 */
	clear(): void {
		this.#currentSnapshot.clear();
	}

	/**
	 * Get the current snapshot for debugging
	 */
	getSnapshot(): Map<string, ObjectSnapshot> {
		return new Map(this.#currentSnapshot);
	}
}
